/**
 * Environment Configuration Utility
 * Provides centralized access to environment variables with type safety and defaults
 */

// Environment variables with defaults
const config = {
  // Application
  app: {
    env: import.meta.env.APP_ENV || import.meta.env.VITE_APP_ENV || 'development',
    // name: import.meta.env.VITE_APP_NAME || 'Nexera System',
    name: import.meta.env.VITE_APP_NAME || 'Al Istedamah Holding',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0'
  },

  // API Configuration
  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api',
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
    backendUrl: import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'
  },

  // Authentication
  auth: {
    tokenKey: import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token',
    userDataKey: import.meta.env.VITE_USER_DATA_KEY || 'user_data',
    storage: import.meta.env.VITE_AUTH_STORAGE || 'localStorage',
    sso: {
      baseUrl: import.meta.env.SSO_BASE_URL || '',
      clientId: import.meta.env.SSO_CLIENT_ID || '',
      realm: import.meta.env.SSO_REALM || '',
      redirectUri: import.meta.env.SSO_REDIRECT_URI || '',
      isPublicClient: ['true', '1', 'yes', 'on'].includes(
        String(import.meta.env.SSO_CLIENT_PUBLIC).toLowerCase()
      )
    }
  },

  // File Upload
  upload: {
    maxFileSize: parseInt(import.meta.env.VITE_MAX_FILE_SIZE) || 10485760, // 10MB
    allowedTypes: import.meta.env.VITE_ALLOWED_FILE_TYPES?.split(',') || ['jpg', 'jpeg', 'png', 'gif', 'pdf']
  },

  // Feature Flags
  features: {
    enableDebug: import.meta.env.VITE_ENABLE_DEBUG === 'true',
    enableConsoleLogs: import.meta.env.VITE_ENABLE_CONSOLE_LOGS === 'true',
    enableErrorTracking: import.meta.env.VITE_ENABLE_ERROR_TRACKING === 'true',
    enableSsoAuditLogs: import.meta.env.VITE_ENABLE_SSO_AUDIT_LOGS === 'true'
  },

  // UI Configuration
  ui: {
    defaultTheme: import.meta.env.VITE_DEFAULT_THEME || 'light',
    enableDarkMode: import.meta.env.VITE_ENABLE_DARK_MODE !== 'false',
    defaultLanguage: import.meta.env.VITE_DEFAULT_LANGUAGE || 'en'
  },

  // Pagination
  pagination: {
    defaultPageSize: parseInt(import.meta.env.VITE_DEFAULT_PAGE_SIZE) || 10,
    maxPageSize: parseInt(import.meta.env.VITE_MAX_PAGE_SIZE) || 100
  },

  // Cache
  cache: {
    duration: parseInt(import.meta.env.VITE_CACHE_DURATION) || 300000, // 5 minutes
    enabled: import.meta.env.VITE_ENABLE_CACHE !== 'false'
  },

  // Security
  security: {
    enableCsrf: import.meta.env.VITE_ENABLE_CSRF !== 'false',
    secureCookies: import.meta.env.VITE_SECURE_COOKIES === 'true',
    sameSiteCookies: import.meta.env.VITE_SAME_SITE_COOKIES || 'lax'
  },

  // CDN (for production)
  cdn: {
    url: import.meta.env.VITE_CDN_URL || '',
    staticUrl: import.meta.env.VITE_STATIC_URL || ''
  },

  // Analytics
  analytics: {
    enabled: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    id: import.meta.env.VITE_ANALYTICS_ID || '',
    errorReportingUrl: import.meta.env.VITE_ERROR_REPORTING_URL || ''
  }
}

/**
 * Environment utility functions
 */
export const env = {
  // Check current environment
  isDevelopment: () => config.app.env === 'development',
  isStaging: () => config.app.env === 'staging',
  isProduction: () => config.app.env === 'production',
  
  // Get environment name
  getEnv: () => config.app.env,
  
  // Debug flag for development
  DEBUG: config.features.enableDebug || config.app.env === 'development',
  
  // Check if feature is enabled
  isFeatureEnabled: (feature) => {
    return config.features[feature] || false
  },
  
  // Get API URL with endpoint
  getApiUrl: (endpoint = '') => {
    const baseUrl = config.api.baseUrl.replace(/\/$/, '') // Remove trailing slash
    const cleanEndpoint = endpoint.replace(/^\//, '') // Remove leading slash
    return cleanEndpoint ? `${baseUrl}/${cleanEndpoint}` : baseUrl
  },
  
  // Get backend URL with path
  getBackendUrl: (path = '') => {
    const baseUrl = config.api.backendUrl.replace(/\/$/, '')
    const cleanPath = path.replace(/^\//, '')
    return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl
  },

  // Get storage instance based on configuration
  getStorage: () => {
    return config.auth.storage === 'sessionStorage' ? sessionStorage : localStorage
  },
  
  // Log only in development
  log: (...args) => {
    if (config.features.enableConsoleLogs) {
      console.log(...args)
    }
  },
  
  // Debug log
  debug: (...args) => {
    if (config.features.enableDebug) {
      console.debug('[DEBUG]', ...args)
    }
  },
  
  // Error log
  error: (...args) => {
    console.error('[ERROR]', ...args)
    
    // Send to error tracking service in production
    if (config.features.enableErrorTracking && config.analytics.errorReportingUrl) {
      // Implementation for error tracking service would go here
      // e.g., Sentry, Bugsnag, etc.
    }
  },
  
  // Warn log
  warn: (...args) => {
    if (config.features.enableConsoleLogs) {
      console.warn('[WARN]', ...args)
    }
  }
}

// Export individual configurations for direct access
export const appConfig = config.app
export const apiConfig = config.api
export const authConfig = config.auth
export const ssoConfig = config.auth.sso
export const uploadConfig = config.upload
export const featureConfig = config.features
export const uiConfig = config.ui
export const paginationConfig = config.pagination
export const cacheConfig = config.cache
export const securityConfig = config.security
export const cdnConfig = config.cdn
export const analyticsConfig = config.analytics

// Export full config as default
export default config
